import { useEffect, useState } from "react";
import { Tag, Trash2, Loader2, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const today = () => new Date().toISOString().split("T")[0];
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().split("T")[0];
const empty = { code: "", description: "", discount_type: "percent", value: 10, valid_from: today(), valid_to: plusDays(30), max_uses: 0, active: true };

export const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/offers").then(({ data }) => setOffers(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/offers", { ...form, value: Number(form.value), max_uses: Number(form.max_uses) });
      toast.success(`Offer ${form.code.toUpperCase()} created`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggle = async (code) => { await api.put(`/offers/${code}/toggle`); load(); };
  const remove = async (code) => {
    if (!window.confirm(`Delete offer ${code}?`)) return;
    await api.delete(`/offers/${code}`); toast.success("Offer deleted"); load();
  };

  const isLive = (o) => o.active && o.valid_from <= today() && o.valid_to >= today();

  return (
    <section className="rounded-2xl border border-border bg-card p-5 lg:p-6" data-testid="admin-offers-section">
      <h2 className="font-serif text-xl font-semibold">Seasonal Offers</h2>
      <p className="text-sm text-muted-foreground mt-1">Create limited-time discount codes guests can apply at booking.</p>

      <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Code</Label>
          <Input required value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="DIWALI20" data-testid="offer-code-input" /></div>
        <div><Label className="text-xs">Description</Label>
          <Input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Diwali special" data-testid="offer-description-input" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Type</Label>
            <select value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="offer-type-select"
              className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"><option value="percent">% off</option><option value="flat">₹ off</option></select></div>
          <div><Label className="text-xs">Value</Label>
            <Input type="number" required min={1} value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="offer-value-input" /></div>
        </div>
        <div><Label className="text-xs">Max uses (0 = unlimited)</Label>
          <Input type="number" min={0} value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} data-testid="offer-max-uses-input" /></div>
        <div><Label className="text-xs">Valid from</Label>
          <Input type="date" required value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} data-testid="offer-from-input" /></div>
        <div><Label className="text-xs">Valid to</Label>
          <Input type="date" required value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} data-testid="offer-to-input" /></div>
        <Button type="submit" disabled={saving} className="gap-1.5 bg-primary hover:bg-primary/90 lg:col-span-2" data-testid="offer-create-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Offer
        </Button>
      </form>

      <div className="mt-5 space-y-2">
        {offers.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No offers yet.</p>}
        {offers.map((o) => (
          <div key={o.code} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3" data-testid={`offer-row-${o.code}`}>
            <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Tag className="h-4 w-4" /></span>
            <div className="flex-1 min-w-[160px]">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold">{o.code}</span>
                <Badge variant="outline" className={isLive(o) ? "bg-secondary/15 text-secondary border-secondary/30" : "text-muted-foreground"}>{isLive(o) ? "Live" : o.active ? "Scheduled/Expired" : "Paused"}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">
                {o.discount_type === "percent" ? `${o.value}% off` : `₹${o.value} off`} · {o.valid_from} → {o.valid_to} · used {o.used_count}{o.max_uses ? `/${o.max_uses}` : ""}
                {o.description && ` · ${o.description}`}
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-xs"><Switch checked={o.active} onCheckedChange={() => toggle(o.code)} data-testid={`offer-toggle-${o.code}`} /> Active</label>
            <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" onClick={() => remove(o.code)} data-testid={`offer-delete-${o.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </section>
  );
};
